import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { folderService } from '../services/folderService';
import { invalidateFolderCache } from '../hooks/useFolderCache';

const FolderContext = createContext();

export const useFolderContext = () => {
  const context = useContext(FolderContext);
  if (!context) {
    throw new Error('useFolderContext must be used within a FolderProvider');
  }
  return context;
};

export const FolderProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [folderTree, setFolderTree] = useState([]);
  const [rootFolder, setRootFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize root folder when user is available
  useEffect(() => {
    if (user?.userId) {
      loadFolderTree();
    }
  }, [user?.userId]);

  const loadFolderTree = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await folderService.getFolderTree();
      const tree = response.data;
      
      setFolderTree(tree);
      
      // Set root folder if available (backend should auto-create if needed)
      if (tree.length > 0) {
        const rootFolder = tree[0];
        setRootFolder(rootFolder);
        
        // Auto-select root folder if no folder is selected
        // This prevents the "Folder" -> "Root Folder" flickering
        if (!selectedFolderId) {
          setSelectedFolderId(rootFolder.id);
        }
      } else {
        // If no folders returned and this is first attempt, retry once after a short delay
        // This handles the case where backend is creating root folder
        if (retryCount === 0) {
          console.log('No folders returned - retrying in case backend is creating root folder');
          setTimeout(() => {
            loadFolderTree(1);
          }, 1000);
          return;
        } else {
          // After retry, if still no folders, log but don't error
          console.log('No folders returned after retry - this may be normal for new users');
          setRootFolder(null);
        }
      }
    } catch (err) {
      console.error('Error loading folder tree:', err);
      setError(err.friendlyMessage || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const selectFolder = (folderId) => {
    setSelectedFolderId(folderId);
  };

  const clearSelection = () => {
    setSelectedFolderId(null);
  };

  // Create folder with automatic parent defaulting
  const createFolder = async (folderData, parentId = null) => {
    try {
      const payload = {
        ...folderData,
        parentId: parentId || rootFolder?.id // Default to root if no parent
      };
      
      const newFolder = await folderService.createFolder(payload);
      
      // Invalidate cache and reload tree to get updated structure
      invalidateFolderCache();
      await loadFolderTree();
      
      return newFolder;
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  };

  // Update folder with root folder restrictions
  const updateFolder = async (folderId, updateData) => {
    try {
      const updatedFolder = await folderService.updateFolder(folderId, updateData);
      
      // Invalidate cache and reload tree to get updated structure
      invalidateFolderCache();
      await loadFolderTree();
      
      return updatedFolder;
    } catch (error) {
      console.error('Error updating folder:', error);
      throw error;
    }
  };

  // Delete folder with root folder protection
  const deleteFolder = async (folderId) => {
    try {
      await folderService.deleteFolder(folderId);
      
      // If deleted folder was selected, select root folder
      if (selectedFolderId === folderId) {
        setSelectedFolderId(rootFolder?.id);
      }
      
      // Invalidate cache and reload tree to get updated structure
      invalidateFolderCache();
      await loadFolderTree();
    } catch (error) {
      console.error('Error deleting folder:', error);
      throw error;
    }
  };

  // Move folder with root folder protection
  const moveFolder = async (folderId, newParentId) => {
    try {
      await folderService.moveFolder(folderId, newParentId);
      
      // Invalidate cache and reload tree to get updated structure
      invalidateFolderCache();
      await loadFolderTree();
    } catch (error) {
      console.error('Error moving folder:', error);
      throw error;
    }
  };

  // Get folder by ID from tree
  const getFolderById = (folderId, folders = folderTree) => {
    for (const folder of folders) {
      if (folder.id === folderId) {
        return folder;
      }
      if (folder.children && folder.children.length > 0) {
        const found = getFolderById(folderId, folder.children);
        if (found) return found;
      }
    }
    return null;
  };

  // Get folder path (breadcrumb)
  const getFolderPath = (folderId, folders = folderTree, path = []) => {
    for (const folder of folders) {
      const currentPath = [...path, folder];
      
      if (folder.id === folderId) {
        return currentPath;
      }
      
      if (folder.children && folder.children.length > 0) {
        const found = getFolderPath(folderId, folder.children, currentPath);
        if (found) return found;
      }
    }
    return [];
  };

  // Check if folder is root folder
  const isRootFolder = (folderId) => {
    return folderId === user?.userId;
  };

  // Get folder actions based on folder type
  const getFolderActions = (folderId) => {
    const folder = getFolderById(folderId);
    if (!folder) return [];
    
    return folderService.getFolderActions(folder, user?.userId);
  };

  const value = {
    // State
    selectedFolderId,
    folderTree,
    rootFolder,
    loading,
    error,
    
    // Actions
    selectFolder,
    clearSelection,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    loadFolderTree,
    
    // Utilities
    getFolderById,
    getFolderPath,
    isRootFolder,
    getFolderActions
  };

  return (
    <FolderContext.Provider value={value}>
      {children}
    </FolderContext.Provider>
  );
};

export default FolderContext;
